import React, { useMemo, memo } from 'react';
import { useIntl } from 'react-intl';
import ReactEcharts from 'echarts-for-react';
import numeral from 'numeral';
import XLogo from 'assets/svgs/twitter-inverted-logo.svg';
import FBLogo from 'assets/svgs/fb-circle-logo.svg';
import InstaLogo from 'assets/svgs/insta-inverted-logo.svg';
import MiscLogo from 'assets/svgs/rss.svg';
import { useAuth } from 'hooks/auth';

interface TermData {
  name: string;
  value: number;
}
interface MostCitedTermsChartProps {
  XAxis: string[];
  title: string;
  legend: string[];
  data: TermData[];
}

const MostCitedTermsChart: React.FC<MostCitedTermsChartProps> = ({
  data,
  title,
  legend,
  XAxis,
}) => {
  const intl = useIntl();
  const { isUserLogged } = useAuth();

  const iconMap = {
    Facebook: FBLogo,
    X: XLogo,
    Instagram: InstaLogo,
    Misc: MiscLogo,
  };

  type socialNetwork = 'Facebook' | 'Instagram' | 'X' | 'Misc';
  const socialNetworks: socialNetwork[] = useMemo(() => {
    if (!isUserLogged) return ['Facebook', 'Instagram', 'Misc'];
    return ['Facebook', 'Instagram', 'X', 'Misc'];
  }, [isUserLogged]);

  const iconGraphics = socialNetworks.map((item, index) => ({
    type: 'image',
    style: {
      image: iconMap[item],
      width: 20,
      height: 20,
    },
    right: 70 + index * 14,
    top: 10,
    z: socialNetworks.length - index,
  }));

  const magicTypeTranslations = useMemo(
    () => ({
      line: intl.formatMessage({ id: 'toolbox.line' }),
      bar: intl.formatMessage({ id: 'toolbox.bar' }),
    }),
    [intl],
  );

  return (
    <ReactEcharts
      ref={e => {
        return e?.getEchartsInstance().setOption({
          baseOption: {
            title: {
              show: true,
              textStyle: {
                fontSize: 16,
              },
            },
            textStyle: {
              fontFamily: 'Lato',
            },
            graphic: iconGraphics,
            grid: {
              top: 42,
              left: 60,
              right: 20,
              bottom: 130,
            },
            legend: {
              data: legend,
              bottom: 40,
              left: 50,
            },
            tooltip: {
              trigger: 'axis',
              axisPointer: {
                type: 'shadow',
              },
            },
            toolbox: {
              show: true,
              bottom: 70,
              right: 10,
              feature: {
                magicType: {
                  show: true,
                  type: ['line', 'bar'],
                  title: magicTypeTranslations,
                  option: {
                    bar: {
                      stack: 'social',
                    },
                    line: {
                      stack: false,
                    },
                  },
                },
              },
            },
            dataZoom: [{ type: 'slider', backgroundColor: '#fff' }],
            xAxis: {
              data: XAxis,
              type: 'category',
              boundaryGap: true,
              axisLine: {
                show: false,
              },
              axisLabel: {
                interval: 0,
              },
            },
            yAxis: {
              type: 'value',
              axisLine: {
                show: false,
              },
              axisLabel: {
                formatter: (value: number) => numeral(value).format('0.[0]a'),
              },
            },
            series: [
              {
                name: title,
                type: 'bar',
                data: XAxis.map(x => {
                  const found = data.find(d => d.name === x);
                  return found ? found.value : 0;
                }),
                itemStyle: {
                  color: '#2A9D90',
                  barBorderRadius: 8,
                },
                barWidth: 37,
              },
            ],
          },
        });
      }}
      option={{}}
      style={{ height: '400px', width: '100%' }}
    />
  );
};

const comparisonFn = (
  prevProps: MostCitedTermsChartProps,
  newProps: MostCitedTermsChartProps,
): boolean => {
  return JSON.stringify(prevProps) === JSON.stringify(newProps);
};

export default memo(MostCitedTermsChart, comparisonFn);
